import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Sparkles, MessageCircle, Heart, MapPin, Clock3, ArrowRight } from "lucide-react";
import MarketChoiceDialog from "@/components/MarketChoiceDialog";
import { BTN_GREEN, BTN_GHOST } from "@/pages/public/Home";
import { NL_IMAGE, RIVIERA_CITIES, NL_AREAS } from "@/lib/riviera";
import { usePageMeta } from "@/lib/seo";

const HERO_IMG = "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920";
const EYEBROW = "text-xs uppercase tracking-[0.22em] font-bold text-[#5BA83F]";
const H2 = "text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight mt-3";
const VALUE_ICONS = [ShieldCheck, Sparkles, MessageCircle, Heart];
const NICE = RIVIERA_CITIES.find((c) => c.slug === "nice");

export default function About() {
  const { t } = useTranslation();
  const [market, setMarket] = useState(null);
  usePageMeta(t("about_v2.seo_title"), t("about_v2.seo_desc"), { canonicalPath: "/about" });
  const ctas = (sfx) => (
    <>
      <button type="button" onClick={() => setMarket("book")} className={BTN_GREEN} data-testid={`about-book-${sfx}`}>{t("about_v2.cta_book")}</button>
      <button type="button" onClick={() => setMarket("quote")} className={BTN_GHOST} data-testid={`about-quote-${sfx}`}>{t("about_v2.cta_quote")}</button>
    </>
  );
  return (
    <div data-testid="about-page">
      <MarketChoiceDialog mode={market} onClose={() => setMarket(null)} />
      <section className="relative overflow-hidden bg-slate-900">
        <img src={HERO_IMG} alt={t("about_v2.hero_alt")} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#102547]/95 via-[#1B3A6B]/80 to-slate-900/40" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 sm:py-20 lg:py-28 tl-reveal">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#9BD478]">{t("about_v2.eyebrow")}</p>
          <h1 className="mt-5 text-[2.2rem] min-[420px]:text-[2.5rem] sm:text-5xl lg:text-6xl text-white leading-[1.08] max-w-3xl [text-wrap:balance]" data-testid="about-title">{t("about_v2.title")}</h1>
          <p className="mt-5 text-[15px] sm:text-lg text-slate-200/90 max-w-xl leading-relaxed">{t("about_v2.subtitle")}</p>
          <div className="mt-8 flex flex-col min-[420px]:flex-row gap-3 [&>*]:w-full min-[420px]:[&>*]:w-auto">{ctas("hero")}</div>
        </div>
      </section>

      <section className="py-14 md:py-20" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5"><p className={EYEBROW}>{t("about_v2.story_eyebrow")}</p><h2 className={H2}>{t("about_v2.story_title")}</h2></div>
          <div className="lg:col-span-7 space-y-4 text-slate-600 leading-relaxed max-w-2xl">
            {(t("about_v2.story", { returnObjects: true }) || []).map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-[#FAF9F6]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className={EYEBROW}>{t("about_v2.values_eyebrow")}</p><h2 className={H2}>{t("about_v2.values_title")}</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(t("about_v2.values", { returnObjects: true }) || []).map((v, i) => {
              const Icon = VALUE_ICONS[i] || Sparkles;
              return (
                <div key={v.t} className="bg-white rounded-2xl p-6 ring-1 ring-[#E5E0D8]" data-testid={`about-value-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#1B3A6B]/5 flex items-center justify-center text-[#1B3A6B]"><Icon className="w-5 h-5" /></div>
                  <h3 className="mt-4 font-bold text-[#0F172A]">{v.t}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <WhereWeOperate t={t} />

      <section className="py-14 md:py-20 bg-[#1B3A6B] text-white" data-testid="about-why">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#9BD478]">{t("about_v2.why_eyebrow")}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">{t("about_v2.why_title")}</h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {(t("about_v2.why", { returnObjects: true }) || []).map((w, i) => (
              <div key={w.t} className="bg-white/5 ring-1 ring-white/10 rounded-2xl p-6" data-testid={`about-why-${i}`}>
                <h3 className="font-bold">{w.t}</h3>
                <p className="mt-1.5 text-sm text-slate-300 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pt-14 md:pt-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="bg-[#102547] rounded-3xl px-8 py-12 md:px-14 md:py-16 text-white relative overflow-hidden">
            <div className="absolute -right-16 -top-16 w-72 h-72 bg-[#5BA83F]/25 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl md:text-5xl leading-tight max-w-2xl">{t("about_v2.cta_title")}</h2>
              <p className="mt-4 text-slate-300 max-w-xl">{t("about_v2.cta_sub")}</p>
              <div className="mt-8 flex flex-col min-[420px]:flex-row gap-3 [&>*]:w-full min-[420px]:[&>*]:w-auto">{ctas("final")}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function WhereWeOperate({ t }) {
  const bookable = RIVIERA_CITIES.filter((c) => !c.inquiryOnly);
  return (
    <section className="py-14 md:py-20" data-testid="about-where">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <p className={EYEBROW}>{t("about_v2.where_eyebrow")}</p><h2 className={H2}>{t("about_v2.where_title")}</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          <RegionCard img={NL_IMAGE} alt={t("about_v2.nl_alt")} title={t("about_v2.nl_title")} desc={t("about_v2.nl_desc")}
            link={{ to: "/service-areas", label: t("about_v2.view_areas") }} testid="about-region-nl" accent="text-[#5BA83F]">
            {NL_AREAS.map((a) => <Pill key={a.slug} to={`/service-areas/${a.slug}`} label={a.city} />)}
          </RegionCard>
          <RegionCard img={NICE.image} alt={t("about_v2.fr_alt")} title={t("about_v2.fr_title")} desc={t("about_v2.fr_desc")}
            link={{ to: "/fr", label: t("about_v2.view_riviera") }} testid="about-region-fr" accent="text-[#0EA5E9]">
            {bookable.map((c) => <Pill key={c.slug} to={`/fr/${c.slug}`} label={c.name} azure />)}
            <p className="w-full mt-2 inline-flex items-start gap-1.5 text-xs text-amber-800" data-testid="about-monaco-note">
              <Clock3 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <Link to="/fr/inquiry?city=monaco" className="underline-offset-2 hover:underline">{t("about_v2.monaco")}</Link>
            </p>
          </RegionCard>
        </div>
      </div>
    </section>
  );
}

function RegionCard({ img, alt, title, desc, link, testid, accent, children }) {
  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-white" data-testid={testid}>
      <img src={img} alt={alt} loading="lazy" className="w-full h-40 md:h-48 object-cover" />
      <div className="p-5 md:p-6">
        <h3 className="text-2xl font-bold text-[#0F172A]">{title}</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
        <div className="mt-4 flex flex-wrap gap-2">{children}</div>
        <Link to={link.to} className={`mt-5 inline-flex items-center gap-1.5 text-sm font-bold ${accent}`}>{link.label} <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}

function Pill({ to, label, azure }) {
  return (
    <Link to={to} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white ring-1 text-sm text-slate-700 transition-colors ${azure ? "ring-sky-200 hover:bg-sky-50" : "ring-slate-200 hover:bg-[#E8F5E4]/60"}`}>
      <MapPin className={`w-3.5 h-3.5 ${azure ? "text-[#0EA5E9]" : "text-[#5BA83F]"}`} /> {label}
    </Link>
  );
}
